import React from "react";
import {
  Box,
  Stack,
  Heading,
  Text,
  List,
  ListItem,
  TextLink,
} from "@origin-digital/ods-core";
import { ConnectionTypeEnum } from "src/components/Changes/types";
import { getAggregatedConnectionTypes } from "../helper";
import {
  AggregatedElectricityConnectionTypes,
  getTimeframes,
} from "./Timeframes";
import { useNewConnectionFees } from "./hooks/useNewConnectionFees";
import FeesByFuelType from "./Fees";
import type { NewConnectionFeeScripting } from "./types";

interface ImportantThingsToKnowProps {
  hasElectricityConnection: boolean;
  hasGasConnection: boolean;
  state: string;
  postcode: string;
  connectionTypes: ConnectionTypeEnum[];
  requestId: string;
}

const ImportantThingsToKnow: React.FC<ImportantThingsToKnowProps> = ({
  hasElectricityConnection,
  hasGasConnection,
  state,
  postcode,
  connectionTypes,
  requestId,
}) => {
  const isVIC = state === "VIC";
  const timeframes = getTimeframes({ postcode });
  const { data: fees } = useNewConnectionFees({
    autoFetch: true,
    requestId,
  });

  const electricityFees =
    (fees
      ?.filter((fee) => fee && "electricity" in fee)
      .map((fee) => fee?.electricity) as NewConnectionFeeScripting[]) ||
    ([] as NewConnectionFeeScripting[]);

  const gasFees =
    (fees
      ?.filter((fee) => fee && "gas" in fee)
      .map((fee) => fee?.gas) as NewConnectionFeeScripting[]) ||
    ([] as NewConnectionFeeScripting[]);

  return (
    <Box backgroundColor="grey50" borderRadius="medium" padding="large">
      <Stack space={["large", "xlarge"]}>
        <Heading variant="h4">Important things to know</Heading>
        {/* Energy charges - not shown if service = gas line only */}
        {(hasElectricityConnection || hasGasConnection) && (
          <Stack space="medium">
            <Text weight="medium" variant="body-small">
              Energy charges
            </Text>
            <Text variant="body-small">
              We will set you up on our Standing Agreement and your prices will
              be our Standing rates. You can find these at{" "}
              <TextLink
                href="https://www.originenergy.com.au/pricing"
                target="_blank"
              >
                originenergy.com.au/pricing
              </TextLink>
              .
            </Text>
            <Text variant="body-small">
              State specific scripting goes here.
            </Text>
          </Stack>
        )}
        {/* End Energy charges */}

        {/* Electricity connection fees */}
        {hasElectricityConnection && (
          <Stack space="medium">
            <Text weight="medium" variant="body-small">
              Electricity connections fees
            </Text>
            <FeesByFuelType
              feesByFuelType={electricityFees}
              fuelType="electricity"
            />
          </Stack>
        )}
        {/* End Electricity connection fees */}

        {/* Natural gas connection fees */}
        {hasGasConnection && (
          <Stack space="medium">
            <Text weight="medium" variant="body-small">
              Natural gas connections fees
            </Text>
            <FeesByFuelType feesByFuelType={gasFees} fuelType="gas" />
          </Stack>
        )}
        {/* End Natural gas connection fees */}

        {/* Additional charges */}
        <Stack space="medium">
          <Text weight="medium" variant="body-small">
            Additional charges
          </Text>
          <Text variant="body-small">
            Other fees or charges that may apply include:
          </Text>
          <List variant="body-small" space="xsmall">
            <ListItem>
              a payment processing fee of the higher of $2.70 or 0.49% (incl
              GST) of the total payment amount (incl GST) for over the counter
              payments made at Australia Post outlets
            </ListItem>
            <ListItem>
              a paper bill fee of $1.75 (incl GST) if you get your bills by
              post, with{" "}
              <TextLink
                href="https://www.originenergy.com.au/for-home/electricity-and-gas/pricing/additional-charges/paper-bill-fee.html"
                target="_blank"
              >
                some exceptions
              </TextLink>
              .
            </ListItem>
          </List>
        </Stack>
        {/* End Additional charges */}

        {/* Installation timeframes */}
        <Stack space="medium">
          <Text weight="medium" variant="body-small">
            Installation timeframes
          </Text>
          <List variant="body-small" space="xsmall">
            {getAggregatedConnectionTypes(connectionTypes, "ELECTRICITY")
              .sort()
              .map((type) => {
                const relevantTimeframes =
                  timeframes[state]?.[
                    type as AggregatedElectricityConnectionTypes
                  ];
                if (!relevantTimeframes) return null;
                return relevantTimeframes;
              })}
          </List>
        </Stack>
        {/* End Installation timeframes */}

        {/* Product and service offers */}
        <Stack space="medium">
          <Text weight="medium" variant="body-small">
            Product and service offers
          </Text>
          <Text variant="body-small">
            From time to time we'll let you know about our great products and
            offers, even after your Agreement ends. You can manage your
            preferences through My Account, alternatively you can call us on{" "}
            <TextLink href="tel:1300 137 932">1300 137 932</TextLink> or write
            to us at Origin Energy Opt Out, Reply Paid 1199, GPO Box 1199,
            Adelaide, SA, 5001.
          </Text>
        </Stack>
        {/* End Product and service offers */}

        {/* Account holder agreement */}
        <Stack space="medium">
          <Text weight="medium" variant="body-small">
            Account holder agreement
          </Text>
          <Text variant="body-small">
            I nominate Origin as the energy retailer for electricity and/or
            natural gas at my property. I understand that:
          </Text>
          <List variant="body-small" space="xsmall">
            <ListItem>
              Once my account's been set up:
              <List>
                <ListItem>
                  {isVIC
                    ? "For electricity or natural gas: Origin will supply energy on a standing agreement and send through all the details, including prices. For electricity, where there is a Victorian Default Offer for your tariff type, this will apply."
                    : "For electricity or natural gas: Origin will supply energy on a standing agreement and send through all the details, including prices. "}
                </ListItem>
                <ListItem>
                  Clear and safe access must be available at the property for
                  connection of natural gas and electricity. If work or delivery
                  cannot be completed due to access restrictions or other
                  issues, an additional fee could apply.
                </ListItem>
              </List>
            </ListItem>
            <ListItem>
              Origin may communicate with my builder and their associated
              contractors in relation to the progress of my account, until my
              connection is complete and, if applicable, the meter is installed.
            </ListItem>
            <ListItem>
              A connection fee may apply, as set out above. This fee will be
              included on my first bill from Origin.
            </ListItem>
          </List>
        </Stack>
        {/* End Account holder agreement */}

        {/* Your privacy */}
        <Stack space="medium">
          <Text weight="medium" variant="body-small">
            Your privacy
          </Text>
          <Text variant="body-small">
            To find out more about how Origin collects and uses your personal
            and credit related information, and what this means for your credit
            rating, go to{" "}
            <TextLink
              href="https://www.originenergy.com.au/privacy"
              target="_blank"
            >
              originenergy.com.au/privacy
            </TextLink>
            .
          </Text>
        </Stack>
        {/* End Your privacy */}
      </Stack>
    </Box>
  );
};

export default ImportantThingsToKnow;
